import { ICianMFUIElement } from '@cian/mf-registry/browser';
import { IMicrofrontendManifest } from '@cian/mf-registry/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useApplicationContext } from '../../../utils/applicationContext';
import {
  INewbuildingBrokerModalsMicrofrontendAPI,
  IOpenOfferBookingModalParams,
} from '../../../types/microfrontends/newbuildingBrokerModals';
import { registerNewbuildingBrokerModalsManifest } from '../../../services/registerNewbuildingBrokerModalsManifest';

type TOpenMicrofrontResultHandler = (isError: boolean) => void;

export function useMicrofrontAPI(resultCallback: TOpenMicrofrontResultHandler) {
  const { httpApi, logger, microfrontendsRegistry } = useApplicationContext();

  const microfrontRef = useRef<ICianMFUIElement>(null);
  const [microfrontManifest, setMicrofrontManifest] = useState<IMicrofrontendManifest>();

  const getLocalAPI = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (): INewbuildingBrokerModalsMicrofrontendAPI => (microfrontRef.current?.instance?.customElement as any)?.api,
    [microfrontRef],
  );

  const paramsRef = useRef<IOpenOfferBookingModalParams | undefined>(undefined);
  const firstAPICallRef = useRef(true);

  useEffect(() => {
    // Логика выполняется один раз при первом вызове локального API после установки манифеста в стейт.
    if (microfrontManifest && firstAPICallRef.current) {
      firstAPICallRef.current = false;

      // Хак, чтобы подождать рендера `<Microfrontend />` и инициализации рефа для локального API.
      setTimeout(() => {
        const api = getLocalAPI();

        if (api) {
          api['/v1/open-offer-booking-modal/'](paramsRef.current as IOpenOfferBookingModalParams);
          resultCallback(false);
        } else {
          resultCallback(true);
        }
      }, 0);
    }
  }, [resultCallback, getLocalAPI, logger, microfrontManifest]);

  const registerAndSetManifest = useCallback(
    async (params: IOpenOfferBookingModalParams) => {
      const result = await registerNewbuildingBrokerModalsManifest({
        httpApi,
        logger,
        microfrontendsRegistry,
      });

      if (result) {
        // Сохраняем в реф, чтобы вызвать с этими параметрами после установки в стейт и инициализации рефа.
        paramsRef.current = params;

        setMicrofrontManifest(result);
      } else {
        resultCallback(true);
      }
    },
    [httpApi, logger, microfrontendsRegistry, resultCallback],
  );

  const openMicrofront = useCallback(
    async (params: IOpenOfferBookingModalParams): Promise<void> => {
      if (microfrontManifest) {
        await getLocalAPI()['/v1/open-offer-booking-modal/'](params);
        resultCallback(false);
      } else {
        await registerAndSetManifest(params);
      }
    },
    [getLocalAPI, registerAndSetManifest, resultCallback, microfrontManifest],
  );

  return {
    microfrontManifest,
    microfrontRef,
    openMicrofront,
  };
}
